import { action, autorun, computed, makeObservable, observable, reaction } from 'mobx';
import {
  freeBetStore,
  gameConfigStore,
  userInfoStore,
} from '@pascal/pascal-core/shared/core/store';
import { iframeService, notificationStore, parseNumberByPrecision } from '@pascal/pascal-core';
import { gameStore } from './gameStore';
import { CHIP_COLORS } from '../enums/chipColor';
import { soundService, soundTypes } from '../services/SoundService';
import { betResponseCodeByName } from '../enums/betResponseCode';
import { cardsStore } from './cardsStore';
import { ADDITIONAL_PARAMS_TYPE, BET_TYPE, CHIP_BET_TYPE } from '../enums/actionTypes';
import { winningAmountStore } from './winningAmountStore';
import { mathConfig, mathJS } from '../helpers/mathFormat';

class DoBetStore {
  selectedChip = 10;

  totalBet = 0;

  reBet = null;

  reBetAmount = 0;

  betsModels = [];

  quickBets = [];

  sideBetsInfo = {};

  isDefaultAmount = true;

  constructor() {
    makeObservable(this, {
      totalBet: observable,
      quickBets: observable,
      reBetAmount: observable,
      selectedChip: observable,
      isDefaultAmount: observable,
      reBet: observable.deep,
      betsModels: observable.deep,
      sideBetsInfo: observable.deep,
      setReBet: action.bound,
      setDouble: action.bound,
      setUndoBet: action.bound,
      setTotalBet: action.bound,
      setBetsModel: action.bound,
      setQuickBets: action.bound,
      setReBetAmount: action.bound,
      resetBetsModels: action.bound,
      setSelectedChip: action.bound,
      updateBetsModel: action.bound,
      setSideBetsInfo: action.bound,
      clearDefaultBets: action.bound,
      updateInsuranceAmount: action.bound,
      setDefaultAmountOnBox: action.bound,
      handleInitialBetsModel: action.bound,
      doubleBetAmountByAction: action.bound,
      updateSurrenderAmountByBox: action.bound,
      updateWinningAmountByRoundResult: action.bound,
      lastBetModel: computed,
      limitsByBetType: computed,
      betsTotalAmount: computed,
    });

    autorun(() => {
      if (freeBetStore.activeFreeBet.id) {
        this.setSelectedChip(freeBetStore.activeFreeBet.amount);
      }
    });

    reaction(
      () => this.betsTotalAmount,
      (value, previousValue) => {
        if (previousValue !== value) {
          iframeService.sendStakeUpdate(value || 0);
        }
      }
    );

    reaction(
      () => gameStore.isBetsClosed,
      (value) => {
        freeBetStore.setIsAllowChangeFreeBet(!value);
      }
    );

    reaction(
      () => freeBetStore.activeFreeBet.id,
      (value, previousValue) => {
        if (previousValue && !value) {
          const { limits } = gameConfigStore;
          this.resetBetsModels();
          this.setReBet(null);
          this.setSelectedChip(limits?.quickBets?.[0]);
        }
        if (previousValue !== value) {
          this.resetBetsModels();
          this.setReBet(null);
        }
      }
    );
  }

  setSelectedChip(value) {
    if (value) {
      iframeService.sendStakeUpdate(value);
    }
    this.selectedChip = value;
  }

  updateWinningAmountByRoundResult(bets) {
    const splitBets = bets.filter(
      (item) => item.additionalParams.type === ADDITIONAL_PARAMS_TYPE.SPLIT_BET
    );
    const doubleBets = bets.filter(
      (item) => item.additionalParams.type === ADDITIONAL_PARAMS_TYPE.DOUBLE
    );
    let betsModel = bets
      .filter((item) => item.additionalParams.markets)
      .map((item) => item.additionalParams);

    betsModel = betsModel.map((item) => {
      const splitBetByBox = splitBets.find((el) => el.additionalParams.boxIndex === item.boxIndex);
      const doubleBetByBox = doubleBets.find(
        (el) => el.additionalParams.boxIndex === item.boxIndex
      );
      const winAmountByBetType = (splitBetByBox || doubleBetByBox)?.winAmount || 0;
      return splitBetByBox || doubleBetByBox
        ? {
            ...item,
            markets: item.markets.map((market) =>
              market.type === ADDITIONAL_PARAMS_TYPE.MAIN_BET
                ? {
                    ...market,
                    winAmount: parseNumberByPrecision(
                      +mathJS.format(market.winAmount + winAmountByBetType, mathConfig),
                      1,
                      +userInfoStore.precision
                    ),
                  }
                : market
            ),
          }
        : item;
    });
    this.betsModels = [{ betModel: betsModel }];
  }

  updateSurrenderAmountByBox(boxIndex) {
    const newBetModel = this.lastBetModel.betModel.map((item) =>
      item.boxIndex === boxIndex
        ? {
            ...item,
            markets: item.markets.map((market) =>
              market.type === ADDITIONAL_PARAMS_TYPE.MAIN_BET
                ? {
                    ...market,
                    amount: parseNumberByPrecision(
                      1,
                      +mathJS.format(market.amount / 2, mathConfig),
                      +userInfoStore.precision
                    ),
                  }
                : { ...market }
            ),
          }
        : { ...item }
    );
    this.betsModels = [{ betModel: newBetModel }];
  }

  handleInitialBetsModel(bets) {
    const keys = Object.keys(bets);
    const betModel = keys.reduce((acc, boxIndex) => {
      const model = bets[boxIndex].boxBets;
      const mainBetModel = model.find((item) => item.mainBet);
      const doubleBet = model.find((item) => item.doubleBet);
      const splitBet = model.find((item) => item.splitBet);
      const sideBets = mainBetModel.mainBet.sideBets.map((item) => ({
        type: item.type,
        amount: item.amount,
      }));
      const sideBetsAmount = sideBets.reduce(
        (accumulator, current) => accumulator + +current.amount,
        0
      );
      const betData = {
        boxIndex: +boxIndex,
        markets: [
          {
            type: ADDITIONAL_PARAMS_TYPE.MAIN_BET,
            amount:
              // eslint-disable-next-line no-nested-ternary
              doubleBet || splitBet
                ? parseNumberByPrecision(
                    1,
                    +mathJS.format(mainBetModel.mainBet.amount * 2, mathConfig),
                    +userInfoStore.precision
                  )
                : mainBetModel.hasMainBetSurrender
                ? parseNumberByPrecision(
                    1,
                    +mathJS.format(mainBetModel.mainBet.amount / 2, mathConfig),
                    +userInfoStore.precision
                  )
                : parseNumberByPrecision(
                    +mathJS.format(mainBetModel.mainBet.amount - sideBetsAmount, mathConfig),
                    1,
                    +userInfoStore.precision
                  ),
          },
          ...sideBets,
        ],
      };
      acc.push(betData);
      return acc;
    }, []);
    this.betsModels = [{ betModel }];
  }

  setSideBetsInfo(sideBetsInfo) {
    this.sideBetsInfo = sideBetsInfo;
  }

  setUndoBet() {
    winningAmountStore.setLastWin(null);
    if (this.betsModels.length) {
      soundService.play(soundTypes.chip);
    }
    this.betsModels.pop();
    if (this.betsModels.length === 1) {
      this.isDefaultAmount = true;
    }
  }

  setBetsModel(betsModel) {
    this.betsModels = [betsModel];
  }

  setDouble() {
    if (this.betsModels.length) {
      const { balance } = userInfoStore;
      const isDoubleNonValid = this.betsModels[this.betsModels.length - 1].betModel
        .reduce((acc, current) => {
          return acc.concat(current.markets);
        }, [])
        .some(({ amount, type }) => amount * 2 > this.limitsByBetType[CHIP_BET_TYPE[type]].maxBet);

      if (isDoubleNonValid) {
        this.handleNotificationCode(betResponseCodeByName.InValidMaxBet);
        return;
      }

      const isDoubleValidForBalance = this.betsTotalAmount * 2 > balance;

      if (isDoubleValidForBalance) {
        this.handleNotificationCode(betResponseCodeByName.NotEnoughBalance);
        return;
      }

      const doubleBetModel = this.betsModels[this.betsModels.length - 1].betModel.map(
        ({ boxIndex, markets }) => ({
          boxIndex,
          markets: markets.map(({ type, amount }) => ({
            type,
            amount: parseNumberByPrecision(
              1,
              +mathJS.format(amount * 2, mathConfig),
              +userInfoStore.precision
            ),
          })),
        })
      );
      this.betsModels = [
        ...this.betsModels,
        {
          betModel: doubleBetModel,
        },
      ];
      this.isDefaultAmount = false;
      soundService.play(soundTypes.chip);
    }
  }

  get lastBetModel() {
    return this.betsModels[this.betsModels.length - 1] || {};
  }

  setReBet(betData) {
    this.reBet = betData;
  }

  setReBetAmount(amount) {
    this.reBetAmount = amount;
  }

  resetBetsModels() {
    this.betsModels = [];
    this.sideBetsInfo = {};
  }

  get limitsByBetType() {
    const {
      limits: { additionalLimits, maxBet },
    } = gameConfigStore;
    const { activeFreeBet } = freeBetStore;
    if (activeFreeBet.id) {
      const freeChipLimits = Object.keys(additionalLimits).reduce(
        (acc, cur) => {
          acc.additionalLimits[cur] = {
            ...additionalLimits[cur],
            maxBet: activeFreeBet.amount > maxBet ? maxBet : activeFreeBet.amount,
          };
          return acc;
        },
        {
          additionalLimits: {},
        }
      );

      return freeChipLimits.additionalLimits;
    }
    return additionalLimits;
  }

  calculateSummary(array) {
    return array.reduce((acc, current) => acc + current);
  }

  get betsTotalAmount() {
    if (this.lastBetModel.betModel?.length) {
      return this.lastBetModel.betModel.reduce((acc, current) => {
        const betModelWithAmount = current.markets.map((item) =>
          parseNumberByPrecision(
            1,
            +mathJS.format(item.amount + (item.insuranceAmount || 0), mathConfig),
            +userInfoStore.precision
          )
        );
        acc = +mathJS.format(acc + this.calculateSummary(betModelWithAmount), mathConfig);
        return parseNumberByPrecision(1, acc, +userInfoStore.precision);
      }, 0);
    }
    return 0;
  }

  createBetDataModelByBox({ boxIndex, type, amount }) {
    return {
      boxIndex,
      markets: [{ type, amount }],
    };
  }

  updateBetsModel(data) {
    const { balance } = userInfoStore;
    const { activeFreeBet } = freeBetStore;

    if (gameStore.isBetsClosed) {
      return;
    }
    if (this.lastBetModel.betModel && activeFreeBet.id) {
      const allBetsCount = this.lastBetModel.betModel.reduce((acc, cur) => {
        cur.markets.forEach(() => {
          acc += 1;
        });
        return acc;
      }, 1);
      if (activeFreeBet.remainingCount - allBetsCount < 0) {
        this.handleNotificationCode(betResponseCodeByName.NotFreeChip);
        return;
      }
    } else if (!this.lastBetModel.betModel && activeFreeBet.id) {
      if (activeFreeBet.remainingCount - 1 < 0) {
        this.handleNotificationCode(betResponseCodeByName.NotFreeChip);
        return;
      }
    }

    if (this.betsTotalAmount + this.selectedChip > balance && !activeFreeBet.id) {
      this.handleNotificationCode(betResponseCodeByName.NotEnoughBalance);
      return;
    }
    this.isDefaultAmount = false;
    const isSideBet = data.type !== BET_TYPE.main;

    if (!this.betsModels.length && !isSideBet) {
      this.betsModels.push({
        betModel: [this.createBetDataModelByBox(data)],
      });
      if (this.betsModels.length === 1) {
        this.isDefaultAmount = true;
      }
      soundService.play(soundTypes.chip);
      return;
    }

    const alreadySelectedBox = this.lastBetModel.betModel?.find(
      (item) => item.boxIndex === data.boxIndex
    );
    const nonSelectedBox = this.lastBetModel.betModel?.filter(
      (item) => item.boxIndex !== data.boxIndex
    );

    if (isSideBet && !alreadySelectedBox) {
      this.handleNotificationCode(betResponseCodeByName.DoChipWarning);
      return;
    }

    if (!alreadySelectedBox) {
      this.betsModels = [
        ...this.betsModels,
        {
          betModel: [...nonSelectedBox, this.createBetDataModelByBox(data)],
        },
      ];
      soundService.play(soundTypes.chip);
      return;
    }

    const alreadySelectedBetType = alreadySelectedBox.markets.find(
      (item) => item.type === data.type
    );
    const nonSelectedBetType = alreadySelectedBox.markets.filter((item) => item.type !== data.type);

    if (!alreadySelectedBetType) {
      if (data.amount > this.limitsByBetType[CHIP_BET_TYPE[data.type]].maxBet) {
        this.handleNotificationCode(betResponseCodeByName.InValidMaxBet);
        return;
      }
      this.betsModels = [
        ...this.betsModels,
        {
          betModel: [
            ...nonSelectedBox,
            {
              ...alreadySelectedBox,
              markets: [...nonSelectedBetType, { type: data.type, amount: data.amount }],
            },
          ],
        },
      ];
      soundService.play(soundTypes.chip);
      return;
    }

    let amount = parseNumberByPrecision(
      1,
      +mathJS.format(alreadySelectedBetType.amount + data.amount, mathConfig),
      +userInfoStore.precision || 2
    );

    if (alreadySelectedBetType.amount === this.limitsByBetType[CHIP_BET_TYPE[data.type]].maxBet) {
      this.handleNotificationCode(betResponseCodeByName.InValidMaxBet);
      return;
    }

    if (amount > this.limitsByBetType[CHIP_BET_TYPE[data.type]].maxBet) {
      amount = this.limitsByBetType[CHIP_BET_TYPE[data.type]].maxBet;
    }

    this.betsModels = [
      ...this.betsModels,
      {
        betModel: [
          ...nonSelectedBox,
          {
            ...alreadySelectedBox,
            markets: [...nonSelectedBetType, { amount, type: data.type }],
          },
        ],
      },
    ];

    soundService.play(soundTypes.chip);
  }

  setDefaultAmountOnBox(defaultAmountIndex = 1) {
    const totalAmountByDefaultQuickBet =
      this.quickBets[defaultAmountIndex].chip * cardsStore.boxCount;
    const totalAmountByMinLimit = this.limitsByBetType.main.minBet * cardsStore.boxCount;
    const { balance } = userInfoStore;
    if (totalAmountByDefaultQuickBet > balance && totalAmountByMinLimit > balance) {
      this.isDefaultAmount = false;
    } else {
      const amount =
        totalAmountByDefaultQuickBet < balance
          ? this.quickBets[defaultAmountIndex].chip
          : this.limitsByBetType.main.minBet;
      const betModel = new Array(cardsStore.boxCount).fill(null).map((item, index) => {
        return this.createBetDataModelByBox({ boxIndex: index, type: BET_TYPE.main, amount });
      });
      this.isDefaultAmount = true;
      this.betsModels.push({ betModel });
      winningAmountStore.setLastWin(0);
    }
  }

  doubleBetAmountByAction(boxIndex) {
    const { betModel } = this.lastBetModel;
    const newBetModel = betModel.map((item) => {
      return item.boxIndex === boxIndex
        ? {
            boxIndex,
            markets: item.markets.map((bet) =>
              bet.type === ADDITIONAL_PARAMS_TYPE.MAIN_BET
                ? {
                    ...bet,
                    amount: parseNumberByPrecision(
                      +mathJS.format(bet.amount * 2, mathConfig),
                      1,
                      +userInfoStore.precision
                    ),
                  }
                : bet
            ),
          }
        : item;
    });
    this.betsModels = [{ betModel: newBetModel }];
  }

  updateInsuranceAmount() {
    const { betModel } = this.lastBetModel;
    const newBetModel = betModel.map((item) => {
      return {
        ...item,
        markets: item.markets.map((bet) =>
          bet.type === ADDITIONAL_PARAMS_TYPE.MAIN_BET
            ? {
                ...bet,
                insuranceAmount: parseNumberByPrecision(
                  +mathJS.format(bet.amount / 2, mathConfig),
                  1,
                  +userInfoStore.precision
                ),
              }
            : bet
        ),
      };
    });
    this.betsModels = [{ betModel: newBetModel }];
  }

  clearDefaultBets() {
    this.betsModels = [];
    this.isDefaultAmount = false;
  }

  setQuickBets(quickBets) {
    this.quickBets = CHIP_COLORS.map((color, index) => ({
      color,
      id: color,
      chip: quickBets[index],
    }));
  }

  setTotalBet(totalBetAmount) {
    this.totalBet = totalBetAmount;
  }

  handleNotificationCode(notification) {
    notificationStore.setNotificationType(notification);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
  }
}

export const doBetStore = new DoBetStore();
